import { createElement, memo, useEffect, useMemo, useRef, useState } from "react";
import { Column } from "@tanstack/table-core";
import { Button } from "../../../../../Button";
import { Dropdown, DropdownListHeader, DropdownListItem } from "../../../../../Dropdown";
import { TextField } from "../../../../../inputs";
import { Tooltip } from "../../../../../Tooltip";
import { renderColumnHeader } from "../../../../utils";

import { ReactComponent as PlusIcon } from "./icons/plus.svg";
import { ReactComponent as SearchIcon } from "./icons/search.svg";

import styles from "./styles.module.scss";

export type AddFilterProps = {
  unusedColumns?: (Column<Object> & { headerTitleForSearch: string })[];
}

export const AddFilter = memo<AddFilterProps>(({ unusedColumns }) => {
  const buttonRef = useRef<HTMLElement>();
  const [ isOpen, setIsOpen ] = useState(false);
  const [ column, setColumn ] = useState<Column<Object, unknown> | null>(null);
  const [ columnsFilter, setColumnsFilter ] = useState<string | null>("");
  const [ tempValue, setTempValue ] = useState<unknown>(null);

  const hasColumnFilter = columnsFilter !== null && columnsFilter !== "";
  const columns = useMemo(
    () => unusedColumns
      ?.filter(column => hasColumnFilter
        ? column.headerTitleForSearch.toLowerCase()
          .includes(columnsFilter.toLowerCase())
        : true,
      ),
    [ columnsFilter, hasColumnFilter, unusedColumns ],
  );

  useEffect(() => {
    if (!isOpen) {
      setColumn(null);
      setTempValue(null);
    }
  }, [ isOpen ]);

  useEffect(() => {
    if (column) {
      setTempValue(null);
    }
  }, [ column ]);

  return (
    <>
      <Tooltip content={"Add filter"}>
        <Button
          buttonSize="md"
          buttonType="secondaryGray"
          onClick={event => setIsOpen(true)}
          iconOnly
          ref={buttonRef}
        >
          <PlusIcon />
        </Button>
      </Tooltip>
      <Dropdown
        floatingOptions={{ placement: "bottom-start" }}
        isOpen={isOpen}
        referenceElement={buttonRef.current}
        onRequestClose={() => setIsOpen(false)}
        closeOnClick="overlay"
        className={styles.dropdown}
        scrollbarProps={!!column ? { className: styles.content } : undefined}
        header={
          <DropdownListHeader>
            {
              column
                ? renderColumnHeader(column)
                : <>
                  <TextField
                    startAdornment={<SearchIcon />}
                    placeholder="Search column options"
                    value={columnsFilter}
                    onChange={setColumnsFilter}
                    isClearable
                  />
                </>
            }
          </DropdownListHeader>
        }
        footer={
          column &&
          <div className={styles.footer}>
            <Button
              buttonSize="md"
              buttonType="tertiaryColor"
              onClick={() => {
                setColumn(null);
              }}
            >
              Back
            </Button>
            <Button
              buttonSize="md"
              buttonType="primary"
              onClick={() => {
                column?.setFilterValue(tempValue);
                setIsOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        }
      >
        {
          column
            ?
            createElement(
              column.columnDef.meta!.Filter!,
              {
                column: column,
                filterFn: column.columnDef.meta!.defaultFilterFn as any,
                value: tempValue,
                onChange: setTempValue,
                autoFocus: true,
              },
            )
            : <>
              {
                !!columns?.length
                  ? columns
                    ?.map(c =>
                      <DropdownListItem
                        key={c.id}
                        component={"button"}
                        onClick={() => setColumn(c)}
                      >
                        {renderColumnHeader(c)}
                      </DropdownListItem>,
                    )
                  : (
                    <DropdownListItem disabled>
                      No columns found
                    </DropdownListItem>
                  )
              }
            </>
        }
      </Dropdown>
    </>
  );
});